import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { UserService } from 'src/app/services/user/user.service';
import { ManagerState } from 'src/app/store/manager/manager.state';
import { advancedStringFilter } from 'src/app/util';
import { UserGroup } from 'src/models';
import {
  isRedundantMentionGroup,
  MentionSuggestion,
  ResolveLabelResult
} from './mention-composer.util';

@Injectable({ providedIn: 'root' })
export class MentionSearchService {
  private searchSerial = 0;

  constructor(
    private readonly userService: UserService,
    private readonly store: Store,
  ) {}

  /** Search users + managed groups for @ autocomplete (same sources as Ask AI). */
  async search(query: string, limit = 8): Promise<MentionSuggestion[]> {
    const serial = ++this.searchSerial;
    const users = await this.userService
      .users({
        detail: 'alias',
        limit,
        sort: 'keywords',
        'filter[keyphrase]': query || '',
        'filter[show-on-reports]': 1,
      })
      .page(0);

    if (serial !== this.searchSerial) {
      return [];
    }

    const normalizedUsers = (users || []).map((u) => ({
      id: u.id,
      type: 'user' as const,
      label: u.name || u.email || u.id,
      secondary: u.email || undefined,
      timezone: typeof u.timezone === 'string' ? u.timezone : undefined,
    }));

    const normalizedGroups = this.filterStoreGroups(query, limit);
    return [...normalizedUsers, ...normalizedGroups].slice(0, Math.max(limit + 2, 10));
  }

  /**
   * Resolve a pasted or typed `@Label` to a managed user or group.
   * Prefers exact name matches; falls back to a single unambiguous search hit.
   * Multiple exact same-type matches are reported as ambiguous (never auto-picked).
   */
  async resolveLabelDetailed(label: string): Promise<ResolveLabelResult> {
    const q = (label ?? '').trim();
    if (!q) {
      return { status: 'unresolved' };
    }
    // Wider window than autocomplete so exact names are less likely to fall off page 0.
    const items = await this.search(q, 25);
    if (!items.length) {
      return { status: 'unresolved' };
    }
    const lower = q.toLowerCase();
    const exact = items.filter((i) => i.label.trim().toLowerCase() === lower);
    if (exact.length === 1) {
      return { status: 'resolved', suggestion: exact[0] };
    }
    if (exact.length > 1) {
      const users = exact.filter((i) => i.type === 'user');
      const groups = exact.filter((i) => i.type === 'group');
      if (users.length === 1 && groups.length >= 1) {
        return { status: 'resolved', suggestion: users[0] };
      }
      if (groups.length === 1 && users.length === 0) {
        return { status: 'resolved', suggestion: groups[0] };
      }
      if (users.length === 1 && groups.length === 0) {
        return { status: 'resolved', suggestion: users[0] };
      }
      return { status: 'ambiguous', candidates: exact };
    }
    if (items.length === 1) {
      return { status: 'resolved', suggestion: items[0] };
    }
    const starts = items.filter((i) =>
      i.label.trim().toLowerCase().startsWith(lower),
    );
    if (starts.length === 1) {
      return { status: 'resolved', suggestion: starts[0] };
    }
    if (starts.length > 1) {
      return { status: 'ambiguous', candidates: starts };
    }
    return { status: 'unresolved' };
  }

  /** Unique match only — ambiguous / missing labels return null. */
  async resolveLabel(label: string): Promise<MentionSuggestion | null> {
    const result = await this.resolveLabelDetailed(label);
    return result.status === 'resolved' ? result.suggestion : null;
  }

  private filterStoreGroups(query: string, limit = 8): MentionSuggestion[] {
    const allGroups: UserGroup[] =
      this.store.selectSnapshot(ManagerState.managedGroups) || [];
    const filtered = query
      ? advancedStringFilter(allGroups, query, (g) => g.name)
      : allGroups;
    return filtered
      .filter((g) => !g.deleted && !isRedundantMentionGroup(g))
      .slice(0, limit)
      .map((g) => ({
        id: g.id,
        type: 'group' as const,
        label: g.name || g.id,
      }));
  }
}
